import * as Sentry from '@sentry/react';
import { reportEventToMonitor } from './index';

/**
 * 收集 console.log 日志的工具
 * 支持发送到 Sentry 和自定义日志服务
 */

// 日志级别
const LOG_LEVELS = {
  LOG: 'log',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  DEBUG: 'debug',
};

// 日志存储（用于批量上报）
let logBuffer = [];
const MAX_BUFFER_SIZE = 50; // 最大缓存数量
const FLUSH_INTERVAL = 5000; // 5秒刷新一次

// 是否启用日志收集
const isLogCollectionEnabled = () => {
  if (typeof window === 'undefined') return false;
  // 可以根据环境变量控制
  return ["production", "sandbox"].includes(process.env.NODE_ENV) || 
         localStorage.getItem('enableLogCollection') === 'true';
};

// 获取北京时间（UTC+8）
export const getBeijingTime = () => {
  const now = new Date();
  // 获取UTC时间戳（毫秒），Date对象内部存储的就是UTC时间戳
  const utcTimestamp = now.getTime();
  // 加上8小时（北京时间 = UTC + 8小时）
  const beijingTimestamp = utcTimestamp + (8 * 60 * 60 * 1000);
  // 创建新的Date对象用于格式化
  const beijingDate = new Date(beijingTimestamp);
  
  // 使用UTC方法获取时间组件（因为我们已经加了8小时，所以UTC方法返回的就是北京时间）
  const year = beijingDate.getUTCFullYear();
  const month = String(beijingDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(beijingDate.getUTCDate()).padStart(2, '0');
  const hours = String(beijingDate.getUTCHours()).padStart(2, '0');
  const minutes = String(beijingDate.getUTCMinutes()).padStart(2, '0');
  const seconds = String(beijingDate.getUTCSeconds()).padStart(2, '0');
  const milliseconds = String(beijingDate.getUTCMilliseconds()).padStart(3, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}+08:00`;
};

// 发送日志到 Sentry
const sendToSentry = (logData) => {
  try {
    // 对于 error 和 warn 级别，使用 captureMessage 发送独立事件，便于在 Sentry Issues 中查看
    if (logData.level === 'error' || logData.level === 'warn') {
      Sentry.captureMessage(logData.message, {
        level: logData.level === 'error' ? 'error' : 'warning',
        tags: {
          logLevel: logData.level,
          tdsKey: logData.tdsKey || 'unknown',
        },
        extra: {
          ...logData,
        },
      });
    }
    
    // 同时作为 Breadcrumb 记录，用于错误事件的上下文
    Sentry.addBreadcrumb({
      category: 'console',
      level: logData.level === 'error' ? 'error' : logData.level === 'warn' ? 'warning' : 'info',
      message: logData.message,
      data: {
        url: logData.url,
        timestamp: logData.timestamp,
        ...logData,
      },
    });
  } catch (e) {
    console.error('发送日志到 Sentry 失败:', e);
  }
};

// 批量上报日志
const flushLogs = () => {
  if (logBuffer.length === 0) return;
  
  const logs = [...logBuffer];
  logBuffer = [];
  
  // 批量发送到 Sentry
  logs.forEach(log => {
    sendToSentry(log);
  });
  
  // 批量发送到自定义服务（可以合并为一次请求）
  if (logs.length > 0) {
    const logSummary = logs
      .map(log => `[${log.level}] ${log.message}`)
      .join('\n');
    
    reportEventToMonitor(
      `前端日志批量上报(${logs.length}条):\n${logSummary}`,
      'LOG',
      'BATCH'
    );
  }
};

// 添加日志到缓冲区
const addToBuffer = (logData) => {
  logBuffer.push(logData);
  
  // 如果缓冲区满了，立即刷新
  if (logBuffer.length >= MAX_BUFFER_SIZE) {
    flushLogs();
  }
};


const setupLogFlush = () => {
  if (typeof window === 'undefined') return;
  
  // 定期刷新日志
  setInterval(flushLogs, FLUSH_INTERVAL);
  
  // 页面卸载时刷新日志
  window.addEventListener('beforeunload', () => {
    flushLogs();
  });
  
  // 页面隐藏时刷新日志（移动端）
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      flushLogs();
    }
  });
};

// 初始化日志收集
export const initLogger = () => {
  if (typeof window === 'undefined') return;
  
  setupLogFlush();
  console.log('前端日志收集已启用');
};

// 手动上报日志（用于特殊场景）
export const reportLog = (level, message, data = {}) => {
  const logData = {
    level,
    message,
    timestamp: getBeijingTime(),
    url: typeof window !== 'undefined' ? window.location.href : '',
    ...data,
  };

  if(!isLogCollectionEnabled()) return;
  
  addToBuffer(logData);
  sendToSentry(logData);
  
};

